import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import StorageIcon from '@mui/icons-material/Storage';
import ArticleIcon from '@mui/icons-material/Article';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CoPresentRoundedIcon from '@mui/icons-material/CoPresentRounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import FlightTakeoffRoundedIcon from '@mui/icons-material/FlightTakeoffRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';

import { useAuth } from "../hooks/useAuth";
import api from "../utils/api";

const mainListItems = [
    { text: 'Dashboard', icon: <HomeRoundedIcon />, link: "/dashboard" },
    { text: 'Users', icon: <ManageAccountsRoundedIcon />, link: "/users" },
    { text: 'Students', icon: <GroupsRoundedIcon />, link: "/students" },
    { text: 'Teachers', icon: <CoPresentRoundedIcon />, link: "/teachers" },
    { text: 'Calendar', icon: <ScheduleRoundedIcon />, link: "/calendar" },
    { text: 'Classrooms', icon: <MeetingRoomRoundedIcon />, link: "/classrooms" },
    { text: 'Purchases', icon: <ShoppingBagRoundedIcon />, link: "/purchased-courses" },
    { text: 'Finance', icon: <AccountBalanceWalletRoundedIcon />, link: "/income-expenses" },
    { text: 'Salaries', icon: <AccountBalanceWalletRoundedIcon />, link: "/salaries" },
    { text: 'Transactions', icon: <ReceiptLongRoundedIcon />, link: "/transactions" },
    { text: 'Statistics', icon: <BarChartRoundedIcon />, link: "/statistics" },
    { text: 'Contact Form', icon: <ContactMailIcon />, link: "/contact-forms" },
    { text: 'Subscribers', icon: <SubscriptionsIcon />, link: "/subscribers" },
    {
        text: 'Settings',
        icon: <SettingsRoundedIcon />,
        children: [
            { text: 'Courses', icon: <SchoolRoundedIcon />, link: "/courses" },
            { text: 'About Page', icon: <PhoneAndroidIcon />, link: "/settings/about-page" },
            { text: 'Study Abroad', icon: <FlightTakeoffRoundedIcon />, link: "/settings/study-abroad" },
            { text: 'Exam Scores', icon: <EmojiEventsRoundedIcon />, link: "/exam-scores" },
            { text: 'Terms & Conditions', icon: <GavelRoundedIcon />, link: "/settings/terms" },
            { text: 'Page SEO', icon: <TravelExploreRoundedIcon />, link: "/settings/page-seo" },
            { text: 'Blog', icon: <ArticleIcon />, link: "/blog" },
            { text: 'Events', icon: <EventRoundedIcon />, link: "/events" },
            { text: 'Branches', icon: <StorefrontRoundedIcon />, link: "/branches" },
            { text: 'Gallery', icon: <PhotoLibraryIcon />, link: "/gallery" },
            { text: 'Testimonials', icon: <RateReviewIcon />, link: "/testimonials" },
            { text: 'Subjects', icon: <AutoStoriesRoundedIcon />, link: "/settings/subjects" },
            { text: 'Categories', icon: <CategoryRoundedIcon />, link: "/settings/categories" },
            { text: 'Lesson Types', icon: <LabelRoundedIcon />, link: "/settings/lesson-types" },
            { text: 'Expense Types', icon: <LabelRoundedIcon />, link: "/settings/expense-types" },
            { text: 'Promocodes', icon: <LocalOfferRoundedIcon />, link: "/settings/promocodes" }
        ]
    },
];

const adminItems = [
    { text: 'Dashboard', icon: <HomeRoundedIcon />, link: "/dashboard" },
    { text: 'Users', icon: <PeopleRoundedIcon />, link: "/users" },
    { text: 'Servers', icon: <StorageIcon />, link: "/servers" },

];

const secondaryListItems = [
    { text: 'Logs', icon: <AssignmentRoundedIcon />, link: "/logs" },
    { text: 'Health', icon: <HeartBrokenIcon />, link: "/health" }
];

export default function MenuContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { _getUser } = useAuth();
    const [openItems, setOpenItems] = useState({});
    const [user, setUser] = useState(_getUser() || null);
    const [unassignedPurchasesCount, setUnassignedPurchasesCount] = useState(0);
    const [pendingContactFormsCount, setPendingContactFormsCount] = useState(0);

    // Check if token is in URL and fetch user data if needed
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tokenFromUrl = urlParams.get('token');
        
        const fetchUserFromToken = async () => {
            if (tokenFromUrl) {
                try {
                    // Fetch user data using the token from URL
                    const response = await api.get('user/me');
                    const userData = response.data.data;
                    // Store in component state only (not localStorage to avoid affecting super admin tab)
                    setUser(userData);
                } catch (error) {
                    console.error('Failed to fetch user from token:', error);
                    // Fallback to localStorage user if fetch fails
                    const storedUser = _getUser();
                    if (storedUser) {
                        setUser(storedUser);
                    }
                }
            } else {
                // Use user from localStorage if no token in URL
                const storedUser = _getUser();
                if (storedUser) {
                    setUser(storedUser);
                }
            }
        };

        fetchUserFromToken();
    }, [location.search, _getUser]);

    useEffect(() => {
        let cancelled = false;
        const fetchSidebarBadges = async () => {
            try {
                const response = await api.get('admin/dashboard/stats');
                const badges = response.data?.data?.badges || [];
                const unassignedPurchases = badges.find((item) => item?.key === 'unassigned_purchases');
                const pendingContactForms = badges.find((item) => item?.key === 'pending_contact_forms');
                if (!cancelled) {
                    setUnassignedPurchasesCount(Number(unassignedPurchases?.count || 0));
                    setPendingContactFormsCount(Number(pendingContactForms?.count || 0));
                }
            } catch (error) {
                if (!cancelled) {
                    setUnassignedPurchasesCount(0);
                    setPendingContactFormsCount(0);
                }
            }
        };
        fetchSidebarBadges();
        return () => {
            cancelled = true;
        };
    }, [location.pathname, location.search]);

    const toggleOpen = (index) => {
        setOpenItems((prev) => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Helper to navigate while preserving token in URL
    const navigateWithToken = (path) => {
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');
        const targetPath = token ? `${path}?token=${token}` : path;
        navigate(targetPath);
    };

    // Helper function to check if a menu item is active
    const isItemActive = (item) => {
        if (!item.link) return false;
        return location.pathname === item.link || location.pathname.startsWith(item.link + '/');
    };

    // Helper function to check if any child is active
    const hasActiveChild = (item) => {
        if (!item.children) return false;
        return item.children.some(child => isItemActive(child));
    };

    const isSuperAdmin = user?.role === 'super_admin';
    const itemsToRender = isSuperAdmin ? adminItems : mainListItems;

    return (
        <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
            <List dense>
                {itemsToRender.map((item, index) => {
                    const isActive = isItemActive(item);
                    const hasActiveChildItem = hasActiveChild(item);
                    const shouldBeOpen = openItems[index] || hasActiveChildItem;
                    
                    return (
                        <ListItem
                            key={index}
                            disablePadding
                            sx={{ display: 'block', color: 'var(---template-palette-text-secondary)' }}>
                            <Link 
                                sx={{ textDecoration: 'none', color: 'inherit' }} 
                                onClick={() => item.link ? navigateWithToken(item.link) : null}
                            >
                                <ListItemButton 
                                    selected={isActive || hasActiveChildItem}
                                    onClick={() => item.children ? toggleOpen(index) : null}
                                    sx={{
                                        backgroundColor: (isActive || hasActiveChildItem) ? 'action.selected' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'action.selected',
                                            '&:hover': {
                                                backgroundColor: 'action.selected',
                                            },
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ color: (isActive || hasActiveChildItem) ? 'primary.main' : 'inherit' }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={
                                            item.link === "/purchased-courses" || item.link === "/contact-forms" ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                                    <Typography variant="inherit">{item.text}</Typography>
                                                    {item.link === "/purchased-courses" && unassignedPurchasesCount > 0 && (
                                                        <Chip
                                                            color="error"
                                                            size="small"
                                                            label={unassignedPurchasesCount}
                                                            sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.75 } }}
                                                        />
                                                    )}
                                                    {item.link === "/contact-forms" && pendingContactFormsCount > 0 && (
                                                        <Chip
                                                            color="warning"
                                                            size="small"
                                                            label={pendingContactFormsCount}
                                                            sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.75 } }}
                                                        />
                                                    )}
                                                </Box>
                                            ) : item.text
                                        }
                                        sx={{
                                            '& .MuiListItemText-primary': {
                                                fontWeight: (isActive || hasActiveChildItem) ? 600 : 400,
                                                color: (isActive || hasActiveChildItem) ? 'primary.main' : 'inherit',
                                            }
                                        }}
                                    />
                                </ListItemButton>
                            </Link>
                        {
                            item.children && (
                                <Collapse in={shouldBeOpen} timeout="auto" unmountOnExit>
                                    <List dense disablePadding sx={{ p: 0, pl: 4, }}>
                                        {item.children.map((child, i) => {
                                            const isChildActive = isItemActive(child);
                                            return (
                                                <Link 
                                                    key={`${index}_${i}`}
                                                    sx={{ textDecoration: 'none', color: 'inherit' }} 
                                                    onClick={() => navigateWithToken(child.link)}
                                                >
                                                    <ListItemButton 
                                                        selected={isChildActive}
                                                        sx={{
                                                            backgroundColor: isChildActive ? 'action.selected' : 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: 'action.hover',
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: 'action.selected',
                                                                '&:hover': {
                                                                    backgroundColor: 'action.selected',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ color: isChildActive ? 'primary.main' : 'inherit' }}>
                                                            {child.icon}
                                                        </ListItemIcon>
                                                        <ListItemText 
                                                            primary={child.text}
                                                            sx={{
                                                                '& .MuiListItemText-primary': {
                                                                    fontWeight: isChildActive ? 600 : 400,
                                                                    color: isChildActive ? 'primary.main' : 'inherit',
                                                                }
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </Link>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            )
                        }

                    </ListItem>
                    );
                })}
            </List>
            {isSuperAdmin && (
                <List dense>
                    {secondaryListItems.map((item, index) => {
                        const isActive = isItemActive(item);
                        return (
                            <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                                <Link 
                                    sx={{ textDecoration: 'none', color: 'inherit' }} 
                                    onClick={() => navigateWithToken(item.link)}
                                >
                                    <ListItemButton 
                                        selected={isActive}
                                        sx={{
                                            backgroundColor: isActive ? 'action.selected' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'action.selected',
                                                '&:hover': {
                                                    backgroundColor: 'action.selected',
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit' }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={item.text}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: isActive ? 600 : 400,
                                                    color: isActive ? 'primary.main' : 'inherit',
                                                }
                                            }}
                                        />
                                    </ListItemButton>
                                </Link>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Stack >
    );
}
